/**
 * Revision Session Hook
 *
 * Manages the lifecycle of a revision session: lazy creation, completion,
 * and abandonment on unmount. Handles the promise queue for concurrent
 * session creation requests.
 *
 * Extracted from useRevisionTracking.ts for single-responsibility clarity.
 */

import { useCallback, useRef, useEffect, useMemo } from 'react';
import {
  createRevision,
  completeRevision,
  type CreateRevisionInput,
} from '@dramadetector/api-client';
import { trackEvent, type UserTier } from '@/lib/analytics/events';
import { scoreToDramaCategory, calculateRevisedScore } from './useScoreCalculation';
import { normalizeError, countByStatus, type TrackingErrorHandler } from './revision-utils';
import type { InlineIssue } from '../types';

interface UseRevisionSessionOptions {
  analysisId: string;
  message: string;
  originalScore: number;
  originalSummary?: string;
  agentExecutionId?: string;
  modelId?: string;
  enabled: boolean;
  tier: UserTier;
  initialIssues: InlineIssue[];
  onTrackingError?: TrackingErrorHandler;
}

interface UseRevisionSessionReturn {
  ensureRevisionSession: () => Promise<string | null>;
  completeSession: (
    issues: InlineIssue[], revisedText: string,
    hasChanges: boolean, serverScore: number | null,
  ) => Promise<void>;
  abandonSession: (issues: InlineIssue[], revisedText: string) => Promise<void>;
  revisionIdRef: React.RefObject<string | null>;
  sessionStartTimeRef: React.RefObject<number | null>;
  decisionLatenciesRef: React.RefObject<number[]>;
  issueDisplayTimesRef: React.RefObject<Map<number, number>>;
}

/** Build the revision payload shared by complete and abandon flows */
function buildRevisionPayload(issues: InlineIssue[], revisedText: string, revisedScore: number) {
  const revisedScoreInt = Math.round(revisedScore);
  return {
    revisedMessage: revisedText,
    revisedScore: revisedScoreInt,
    revisedCategory: scoreToDramaCategory(revisedScoreInt),
    revisedIssues: issues
      .filter((i) => i.status !== 'accepted')
      .map((i) => ({ id: i.id, type: i.type, severity: i.severity, snippet: i.snippet })),
    issuesResolved: issues.filter((i) => i.status === 'accepted').length,
  };
}

export function useRevisionSession({
  analysisId, message, originalScore, originalSummary,
  agentExecutionId, modelId, enabled, tier, initialIssues, onTrackingError,
}: UseRevisionSessionOptions): UseRevisionSessionReturn {
  const revisionIdRef = useRef<string | null>(null);
  const isCreatingRef = useRef(false);
  const pendingQueueRef = useRef<Array<() => Promise<void>>>([]);
  const sessionStartTimeRef = useRef<number | null>(null);
  const decisionLatenciesRef = useRef<number[]>([]);
  const issueDisplayTimesRef = useRef<Map<number, number>>(new Map());

  const originalIssuesDb = useMemo(() => initialIssues.map((issue) => ({
    id: issue.id, type: issue.type, severity: issue.severity, snippet: issue.snippet,
    replacement: issue.replacement, startIndex: issue.startIndex,
    endIndex: issue.endIndex, description: issue.description,
  })), [initialIssues]);

  useEffect(() => {
    const now = Date.now();
    // Clear and rebuild to avoid stale entries from previous issue sets
    issueDisplayTimesRef.current = new Map(
      initialIssues.map((issue) => [issue.id, now])
    );
  }, [initialIssues]);

  const ensureRevisionSession = useCallback(async (): Promise<string | null> => {
    if (!enabled) return null;
    if (revisionIdRef.current) return revisionIdRef.current;

    if (isCreatingRef.current) {
      return new Promise((resolve) => {
        pendingQueueRef.current.push(async () => resolve(revisionIdRef.current));
      });
    }

    isCreatingRef.current = true;
    sessionStartTimeRef.current = Date.now();

    try {
      const input: CreateRevisionInput = {
        analysisId, originalMessage: message, originalScore,
        originalCategory: scoreToDramaCategory(originalScore),
        originalIssues: originalIssuesDb, originalSummary,
        agentExecutionId, modelId, issuesTotal: initialIssues.length,
      };

      const { data, error } = await createRevision(input);

      if (error) {
        if (error.code === 'ANALYSIS_NOT_FOUND') {
          console.info('[RevisionTracking] Skipping tracking for cached/anonymous analysis:', analysisId);
          return null;
        }
        console.error('[RevisionTracking] Failed to create session:', error);
        onTrackingError?.(new Error(error.message), 'createRevision');
        trackEvent('Revision_Tracking_Error', {
          context: 'createRevision', error_message: error.message, analysis_id: analysisId, tier,
        });
        return null;
      }

      revisionIdRef.current = data?.id ?? null;

      if (revisionIdRef.current) {
        trackEvent('Revision_Session_Created', {
          revision_id: revisionIdRef.current, analysis_id: analysisId,
          original_score: originalScore, original_category: scoreToDramaCategory(originalScore),
          issues_total: initialIssues.length, tier, model_id: modelId,
        });
      }

      const queue = pendingQueueRef.current;
      pendingQueueRef.current = [];
      const results = await Promise.allSettled(queue.map((fn) => fn()));
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`[RevisionTracking] Queued task ${index} failed:`, result.reason);
          const queueError = result.reason instanceof Error ? result.reason : new Error(String(result.reason));
          onTrackingError?.(queueError, 'queueProcessing');
          trackEvent('Revision_Tracking_Error', {
            context: 'queueProcessing' as const, error_message: queueError.message,
            revision_id: revisionIdRef.current ?? undefined, tier,
          });
        }
      });

      return revisionIdRef.current;
    } catch (err) {
      console.error('[RevisionTracking] Session creation error:', err);
      const { error: normalizedErr, message: errorMessage } = normalizeError(err);
      onTrackingError?.(normalizedErr, 'createRevision');
      trackEvent('Revision_Tracking_Error', {
        context: 'createRevision', error_message: errorMessage, analysis_id: analysisId, tier,
      });
      return null;
    } finally {
      isCreatingRef.current = false;
    }
  }, [enabled, analysisId, message, originalScore, originalIssuesDb, originalSummary,
    agentExecutionId, modelId, initialIssues.length, tier, onTrackingError]);

  const completeSession = useCallback(
    async (issues: InlineIssue[], revisedText: string, hasChanges: boolean, serverScore: number | null) => {
      if (!enabled || !revisionIdRef.current || !hasChanges) return;
      const sessionId = revisionIdRef.current;

      try {
        const issuesAccepted = countByStatus(issues, 'accepted');
        const issuesIgnored = countByStatus(issues, 'ignored');
        const revisedScore = serverScore !== null
          ? serverScore
          : calculateRevisedScore(originalScore, issuesAccepted, initialIssues.length);

        const sessionDurationMs = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : undefined;
        const latencies = decisionLatenciesRef.current;
        const avgLatencyMs = latencies.length > 0
          ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length) : undefined;

        const { error } = await completeRevision(sessionId, buildRevisionPayload(issues, revisedText, revisedScore));

        if (error) {
          console.error('[RevisionTracking] Failed to complete session:', error);
          onTrackingError?.(new Error(error.message), 'completeRevision');
          trackEvent('Revision_Tracking_Error', {
            context: 'completeRevision', error_message: error.message, revision_id: sessionId, tier,
          });
        } else {
          trackEvent('Revision_Session_Completed', {
            revision_id: sessionId, issues_accepted: issuesAccepted, issues_ignored: issuesIgnored,
            issues_total: initialIssues.length, original_score: originalScore,
            revised_score: revisedScore, score_delta: originalScore - revisedScore,
            session_duration_ms: sessionDurationMs ?? 0, avg_decision_latency_ms: avgLatencyMs, tier,
          });
        }
      } catch (err) {
        console.error('[RevisionTracking] Failed to complete session:', err);
        const { error: normalizedErr, message: errorMessage } = normalizeError(err);
        onTrackingError?.(normalizedErr, 'completeRevision');
        trackEvent('Revision_Tracking_Error', {
          context: 'completeRevision', error_message: errorMessage, revision_id: sessionId, tier,
        });
      }
    },
    [enabled, originalScore, initialIssues.length, tier, onTrackingError]
  );

  const abandonSession = useCallback(
    async (issues: InlineIssue[], revisedText: string) => {
      if (!enabled || !revisionIdRef.current) return;
      const sessionId = revisionIdRef.current;
      const issuesAccepted = countByStatus(issues, 'accepted');
      const issuesIgnored = countByStatus(issues, 'ignored');
      const revisedScore = calculateRevisedScore(originalScore, issuesAccepted, initialIssues.length);

      try {
        const { error } = await completeRevision(
          sessionId, buildRevisionPayload(issues, revisedText, revisedScore), 'abandoned'
        );

        if (error) {
          console.error('[RevisionTracking] Failed to abandon session:', error);
        } else {
          const sessionDurationMs = sessionStartTimeRef.current ? Date.now() - sessionStartTimeRef.current : undefined;
          trackEvent('Revision_Session_Completed', {
            revision_id: sessionId, issues_accepted: issuesAccepted, issues_ignored: issuesIgnored,
            issues_total: initialIssues.length, original_score: originalScore,
            revised_score: revisedScore, score_delta: originalScore - revisedScore,
            session_duration_ms: sessionDurationMs ?? 0, avg_decision_latency_ms: undefined, tier,
          });
        }
      } catch (err) {
        console.error('[RevisionTracking] Failed to abandon session:', err);
      }
    },
    [enabled, originalScore, initialIssues.length, tier]
  );

  // Cleanup: resolve any pending queue promises on unmount to prevent leaks
  useEffect(() => {
    return () => {
      const queue = pendingQueueRef.current;
      pendingQueueRef.current = [];
      // Resolve pending promises with null (session won't exist)
      queue.forEach((fn) => fn().catch((err) => {
        console.warn('[RevisionTracking] Queue cleanup error on unmount:', err);
      }));
    };
  }, []);

  return {
    ensureRevisionSession, completeSession, abandonSession,
    revisionIdRef, sessionStartTimeRef, decisionLatenciesRef, issueDisplayTimesRef,
  };
}
